import { useMemo, useState } from 'react';
import './ProjectVisualization.css';

const TYPE_COLORS = ['#2f80ed', '#16866f', '#d9822b', '#8e62b0', '#d24b4b', '#008a9a'];

const UNIT_STATUS = {
  DRAFT: { label: 'پیش‌نویس', color: '#8a9690' },
  PLANNED: { label: 'برنامه‌ریزی', color: '#65778f' },
  CONSTRUCTION: { label: 'در حال ساخت', color: '#d9822b' },
  AVAILABLE: { label: 'قابل عرضه', color: '#16866f' },
  SOLD: { label: 'فروخته شده', color: '#315c48' },
  CANCELLED: { label: 'لغو شده', color: '#b14c45' }
};

const formatMoney = (value) => {
  const number = Number(value);
  return Number.isFinite(number) ? number.toLocaleString('fa-IR') : '۰';
};

function closestSection(sectionId, type, sectionById) {
  let current = sectionById.get(sectionId);
  const visited = new Set();
  while (current && !visited.has(current.id)) {
    if (current.type === type) return current;
    visited.add(current.id);
    current = sectionById.get(current.parent);
  }
  return null;
}

function compareNames(a, b) {
  return String(a.name).localeCompare(String(b.name), 'fa', { numeric: true });
}

function buildSceneHierarchy(sections, units) {
  const sectionById = new Map(sections.map((section) => [section.id, section]));
  const phaseMap = new Map();

  const ensurePhase = (phase) => {
    const key = phase?.id || '__project';
    if (!phaseMap.has(key)) {
      phaseMap.set(key, {
        id: key,
        name: phase?.name || 'کل پروژه',
        synthetic: !phase,
        blocks: new Map()
      });
    }
    return phaseMap.get(key);
  };

  const ensureBlock = (phase, block) => {
    const key = block?.id || `${phase.id}:direct`;
    if (!phase.blocks.has(key)) {
      phase.blocks.set(key, {
        id: key,
        name: block?.name || 'محدوده مستقیم فاز',
        synthetic: !block,
        buildings: new Map()
      });
    }
    return phase.blocks.get(key);
  };

  const ensureBuilding = (block, building, fallbackName = 'واحدهای مستقیم') => {
    const key = building?.id || `${block.id}:direct`;
    if (!block.buildings.has(key)) {
      block.buildings.set(key, {
        id: key,
        name: building?.name || fallbackName,
        synthetic: !building,
        units: []
      });
    }
    return block.buildings.get(key);
  };

  sections.filter((section) => section.type === 'PHASE').forEach((phase) => ensurePhase(phase));
  sections.filter((section) => section.type === 'BLOCK').forEach((block) => {
    const phase = ensurePhase(closestSection(block.parent, 'PHASE', sectionById));
    ensureBlock(phase, block);
  });
  sections.filter((section) => section.type === 'BUILDING').forEach((building) => {
    const phase = ensurePhase(closestSection(building.parent, 'PHASE', sectionById));
    const block = ensureBlock(phase, closestSection(building.parent, 'BLOCK', sectionById));
    ensureBuilding(block, building);
  });

  units.forEach((unit) => {
    const phaseSection = closestSection(unit.section, 'PHASE', sectionById);
    const blockSection = closestSection(unit.section, 'BLOCK', sectionById);
    const buildingSection = closestSection(unit.section, 'BUILDING', sectionById);
    const phase = ensurePhase(phaseSection);
    const block = ensureBlock(phase, blockSection);
    const fallbackName = buildingSection ? buildingSection.name : (blockSection ? 'واحدهای مستقیم بلوک' : 'واحدهای مستقیم پروژه');
    ensureBuilding(block, buildingSection, fallbackName).units.push(unit);
  });

  return [...phaseMap.values()]
    .sort(compareNames)
    .map((phase) => ({
      ...phase,
      blocks: [...phase.blocks.values()]
        .sort(compareNames)
        .map((block) => ({
          ...block,
          buildings: [...block.buildings.values()]
            .sort(compareNames)
            .map((building) => {
              const floorMap = new Map();
              building.units.forEach((unit) => {
                const floorKey = unit.floor === null || unit.floor === undefined ? '__none' : String(unit.floor);
                if (!floorMap.has(floorKey)) floorMap.set(floorKey, []);
                floorMap.get(floorKey).push(unit);
              });
              const floors = [...floorMap.entries()]
                .sort(([a], [b]) => {
                  if (a === '__none') return 1;
                  if (b === '__none') return -1;
                  return Number(b) - Number(a);
                })
                .map(([floor, floorUnits]) => ({
                  id: `${building.id}:floor:${floor}`,
                  floor,
                  units: floorUnits.sort((a, b) => String(a.unit_number).localeCompare(String(b.unit_number), 'fa', { numeric: true }))
                }));
              return { ...building, floors };
            })
        }))
    }));
}

function scopeUnitIds(scope) {
  if (scope.units) return scope.units.map((unit) => unit.id);
  if (scope.floors) return scope.floors.flatMap((floor) => floor.units.map((unit) => unit.id));
  if (scope.buildings) return scope.buildings.flatMap(scopeUnitIds);
  if (scope.blocks) return scope.blocks.flatMap(scopeUnitIds);
  return [];
}

function selectionState(ids, selectedIds) {
  if (!ids.length) return 'empty';
  const selectedCount = ids.filter((id) => selectedIds.has(id)).length;
  if (!selectedCount) return 'none';
  return selectedCount === ids.length ? 'all' : 'partial';
}

function ScopeButton({ children, className, ids, selectable, selectedIds, onToggle }) {
  const state = selectionState(ids, selectedIds);
  return (
    <button
      type="button"
      className={`${className} selection-${state}`}
      disabled={!selectable || !ids.length}
      aria-pressed={state === 'all'}
      onClick={() => onToggle(ids)}
    >
      {children}
      <small>{ids.length.toLocaleString('fa-IR')} واحد</small>
    </button>
  );
}

export default function ProjectVisualization({
  sections = [],
  templates = [],
  units = [],
  strategies = [],
  selectable = false,
  selectedUnitIds = [],
  onSelectionChange,
  defaultOverlay = 'structure',
  pricePerMeter = '',
  className = ''
}) {
  const [overlay, setOverlay] = useState(defaultOverlay);
  const hierarchy = useMemo(() => buildSceneHierarchy(sections, units), [sections, units]);
  const unitIds = useMemo(() => new Set(units.map((unit) => unit.id)), [units]);
  const selectedIds = useMemo(
    () => new Set(selectedUnitIds.filter((unitId) => unitIds.has(unitId))),
    [selectedUnitIds, unitIds]
  );
  const strategyByUnit = useMemo(() => new Map(strategies.map((strategy) => [strategy.unit, strategy])), [strategies]);
  const typeByRevision = useMemo(() => {
    const result = new Map();
    templates.forEach((template, index) => (template.revisions || []).forEach((revision) => {
      result.set(revision.id, { label: template.name, color: TYPE_COLORS[index % TYPE_COLORS.length] });
    }));
    return result;
  }, [templates]);

  const toggleIds = (ids) => {
    if (!selectable || !onSelectionChange || !ids.length) return;
    const allSelected = ids.every((id) => selectedIds.has(id));
    const next = new Set(selectedIds);
    ids.forEach((id) => allSelected ? next.delete(id) : next.add(id));
    onSelectionChange([...next]);
  };

  const unitVisual = (unit) => {
    if (selectedIds.has(unit.id)) return { color: '#2f80ed', label: 'انتخاب شده', className: 'is-selected' };
    if (overlay === 'sales') {
      return strategyByUnit.has(unit.id)
        ? { color: '#16866f', label: 'دارای شرایط فروش', className: 'has-strategy' }
        : { color: '#aeb8b3', label: 'بدون شرایط فروش', className: 'no-strategy' };
    }
    if (overlay === 'status') {
      const status = UNIT_STATUS[unit.status] || UNIT_STATUS.DRAFT;
      return { color: status.color, label: status.label, className: `status-${String(unit.status || 'DRAFT').toLowerCase()}` };
    }
    const type = typeByRevision.get(unit.template_revision);
    return { color: type?.color || '#8a9690', label: type?.label || unit.template_name || 'تیپ نامشخص', className: 'by-type' };
  };

  const unitTitle = (unit) => {
    const strategy = strategyByUnit.get(unit.id);
    const previewPrice = Number(pricePerMeter) * Number(unit.area);
    const details = [
      `واحد ${unit.unit_number}`,
      unit.floor === null || unit.floor === undefined ? 'بدون طبقه' : `طبقه ${unit.floor}`,
      unit.area ? `${Number(unit.area).toLocaleString('fa-IR')} متر مربع` : null,
      `وضعیت: ${unitVisual(unit).label}`
    ];
    if (strategy?.price_per_meter) details.push(`هر متر: ${formatMoney(strategy.price_per_meter)} تومان`);
    if (strategy?.price) details.push(`قیمت کل: ${formatMoney(strategy.price)} تومان`);
    if (!strategy && Number.isFinite(previewPrice) && previewPrice > 0) details.push(`قیمت محاسبه‌شده: ${formatMoney(previewPrice)} تومان`);
    return details.filter(Boolean).join(' | ');
  };

  const overlayLegend = overlay === 'sales'
    ? [
      { label: 'انتخاب شده', color: '#2f80ed' },
      { label: 'دارای شرایط فروش', color: '#16866f' },
      { label: 'بدون شرایط فروش', color: '#aeb8b3' }
    ]
    : overlay === 'status'
      ? Object.values(UNIT_STATUS)
      : templates.slice(0, TYPE_COLORS.length).map((template, index) => ({ label: template.name, color: TYPE_COLORS[index] }));

  return (
    <section className={`project-visualization ${className}`} aria-label="نمای فضایی پروژه">
      <header className="project-visualization__head">
        <div>
          <span className="project-visualization__eyebrow">مدل تعاملی پروژه</span>
          <h2>انتخاب و پایش واحدها</h2>
        </div>
        <div className="project-visualization__controls">
          <div className="project-visualization__tabs" aria-label="نوع نمایش">
            <button type="button" className={overlay === 'structure' ? 'active' : ''} onClick={() => setOverlay('structure')}>ساختار</button>
            <button type="button" className={overlay === 'sales' ? 'active' : ''} onClick={() => setOverlay('sales')}>فروش</button>
            <button type="button" className={overlay === 'status' ? 'active' : ''} onClick={() => setOverlay('status')}>وضعیت واحد</button>
          </div>
          <span className="project-visualization__selection">
            {selectable ? `${selectedIds.size.toLocaleString('fa-IR')} واحد انتخاب شده` : `${units.length.toLocaleString('fa-IR')} واحد`}
          </span>
        </div>
      </header>

      <div className="project-visualization__scene">
        {!sections.length && !units.length && <p className="project-visualization__empty-scene">با تعریف ساختار و واحدها، مدل پروژه در این بخش شکل می‌گیرد.</p>}
        {hierarchy.map((phase) => {
          const phaseIds = scopeUnitIds(phase);
          return (
            <section className="project-visualization__phase" key={phase.id}>
              <ScopeButton className="project-visualization__phase-button" ids={phaseIds} selectable={selectable} selectedIds={selectedIds} onToggle={toggleIds}>
                <span>فاز</span>{phase.name}
              </ScopeButton>
              <div className="project-visualization__blocks">
                {phase.blocks.map((block) => {
                  const blockIds = scopeUnitIds(block);
                  return (
                    <section className="project-visualization__block" key={block.id}>
                      <ScopeButton className="project-visualization__block-button" ids={blockIds} selectable={selectable} selectedIds={selectedIds} onToggle={toggleIds}>
                        <span>{block.synthetic ? 'محدوده' : 'بلوک'}</span>{block.name}
                      </ScopeButton>
                      <div className="project-visualization__buildings">
                        {block.buildings.map((building) => {
                          const buildingIds = scopeUnitIds(building);
                          return (
                            <article className="project-visualization__building-wrap" key={building.id}>
                              <ScopeButton className="project-visualization__building-button" ids={buildingIds} selectable={selectable} selectedIds={selectedIds} onToggle={toggleIds}>
                                <span>{building.synthetic ? 'گروه' : 'ساختمان'}</span>{building.name}
                              </ScopeButton>
                              <div className={`project-visualization__tower selection-${selectionState(buildingIds, selectedIds)}`}>
                                {building.floors.map((floor) => {
                                  const floorIds = floor.units.map((unit) => unit.id);
                                  return (
                                    <div className={`project-visualization__floor selection-${selectionState(floorIds, selectedIds)}`} key={floor.id}>
                                      <button type="button" className="project-visualization__floor-button" disabled={!selectable} onClick={() => toggleIds(floorIds)}>
                                        {floor.floor === '__none' ? 'ـ' : floor.floor}
                                      </button>
                                      <div className="project-visualization__units">
                                        {floor.units.map((unit) => {
                                          const visual = unitVisual(unit);
                                          return (
                                            <button
                                              type="button"
                                              className={`project-visualization__unit ${visual.className}`}
                                              style={{ '--unit-color': visual.color }}
                                              key={unit.id}
                                              disabled={!selectable}
                                              aria-pressed={selectedIds.has(unit.id)}
                                              onClick={() => toggleIds([unit.id])}
                                              title={unitTitle(unit)}
                                            >
                                              {unit.unit_number}
                                            </button>
                                          );
                                        })}
                                      </div>
                                    </div>
                                  );
                                })}
                                {!building.floors.length && <div className="project-visualization__empty-building">بدون واحد</div>}
                              </div>
                            </article>
                          );
                        })}
                      </div>
                    </section>
                  );
                })}
              </div>
            </section>
          );
        })}
      </div>

      <footer className="project-visualization__footer">
        <div className="project-visualization__legend">
          {overlayLegend.map((item) => <span key={item.label}><i style={{ background: item.color }} />{item.label}</span>)}
          {!overlayLegend.length && <span>هنوز داده‌ای برای این نما ثبت نشده است.</span>}
        </div>
        {selectable && <small>برای انتخاب گروهی روی نام فاز، بلوک، ساختمان یا شماره طبقه کلیک کنید.</small>}
      </footer>
    </section>
  );
}
